import React, { useState, useEffect, useCallback, useContext } from 'react';
import ReactPaginate from 'react-paginate';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';
import {
  ChangeSetPointsValueConfirmation,
  Loader,
} from '../../../../components';
import { newFormatDate, notifyError, notifySuccess } from '../../../../utils';
import {
  getThingCommands,
  getCommandHistoric,
  putThingCommandV2,
  getCommandsSetPoints,
  putSetPointsValue,
  getPendingCommand,
} from '../../../../services';
import {
  Container,
  Caption,
  Content,
  Label,
  ModalContainer,
  CloseButton,
  CommandsWrapper,
  Command,
  HistoricTimelineWrapper,
  HistoricTimelineCell,
  HistoricCellColumn,
  Line,
  Column,
  Dot,
  getModalStyles,
  SetPointInputContainer,
  SetPointItem,
  ContainerElements,
  PendingCommandBanner,
} from './styles';
import '../../../../styles/pagination.css'

// locked / historyOpen / setHistoryOpen vem do List (RailHead). Antes
// o Commands tinha seu proprio header com cadeado (PNG) + historico, que
// duplicava os icones decorativos do RailHead. Agora os icones do
// RailHead acionam de fato a trava e o modal, e este componente so
// renderiza os comandos + setpoints + o modal de historico.
const Commands = ({ thing, locked = true, historyOpen, setHistoryOpen }) => {
  const { user } = useSelector((state) => state.usuario);
  const [commandsList, setCommandsList] = useState([]);
  const [commandHistoric, setCommandHistoric] = useState([]);
  const [loading, setLoading] = useState(true);
  const [valueToChangeSetpoint, setValueToChangeSetpoint] = useState(false);
  const [savingSetpointChanges, setSavingSetpointChanges] = useState(false);
  // Comando em envio (id) — desabilita o botao + feedback "Enviando..." pra
  // o operador nao clicar 2x num link ruim. Antes nao havia loading/disable.
  const [sendingCommandId, setSendingCommandId] = useState(null);
  // Comando pendente no DISPOSITIVO (delta do shadow — rearme/habilitacao que
  // o device ainda nao confirmou). Util quando a conexao IoT esta ruim: avisa
  // o operador "ha comando pendente desde X" em vez de parecer que sumiu.
  const [pendingCommand, setPendingCommand] = useState(null);
  // Comando aguardando CONFIRMACAO do operador ({id, value, name}) — modal
  // dupla-confirmacao antes de enviar a maquina fisica (mesmo padrao do
  // setpoint). null = nenhum.
  const [confirmCommand, setConfirmCommand] = useState(null);
  const [setPointsWidthEdition, setSetPointsWidthEdition] = useState([]);
  const thingId = Number(thing.id);
  const USER_ALLOWED_TO_CHANGE_SETPOINTS = user.role !== 'visualizador';

  const [amount, setAmount] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const itemsPerPage = 50

  const { title, colors } = useContext(ThemeContext);

  useEffect(() => {
    getThingCommands(thingId)
      .then(({ data }) => {
        setCommandsList(data);
      })
      .catch(() => null)
      .finally(() => setLoading(false));

    searchHistory();

    // Comando pendente no device (on-demand, 1 call). Silencioso em erro —
    // banner so aparece quando ha pendencia real.
    getPendingCommand(thingId)
      .then(({ data }) => {
        if (data && data.hasPending && data.pending && data.pending.length) {
          setPendingCommand(data.pending[0]);
        }
      })
      .catch(() => null);
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    getCommandsSetPoints(thingId)
      .then(({ data }) => {
        setSetPointsWidthEdition(data);
      })
      .catch(() => null)
      .finally(() => setLoading(false));
      /* eslint-disable-next-line */
  }, []);

  const handlePageClick = (event) => {
    const offSet = event.selected * 50
    setCurrentPage(event.selected)

    searchHistory(offSet)
  }

  const searchHistory = (offSet) => {
    getCommandHistoric(thingId, offSet)
    .then(({ data }) => {
      setCommandHistoric(data.items);
      setAmount(data.totalItems)
    })
    .catch(() => setCommandHistoric([]))
  };

  const handleCommand = (commandId, value) => {
    if (sendingCommandId != null) return; // ja ha um envio em curso
    setConfirmCommand(null);
    setSendingCommandId(commandId);
    putThingCommandV2({
      commandGroup: 'ALARME',
      commandId: parseInt(commandId, 10),
      thingId: parseInt(thingId, 10),
      value,
    })
      .then(() => {
        // "enviado" (despachado ao broker) — NAO afirma execucao no device,
        // que so se confirma de forma assincrona (conexao do equipamento).
        notifySuccess('Comando enviado ao dispositivo');
        searchHistory();
      })
      .catch(() => notifyError('Erro ao enviar o comando'))
      .finally(() => setSendingCommandId(null));
  };

  const handleChangeSetpointValue = useCallback(() => {
    if (Number.isNaN(Number(valueToChangeSetpoint.value)))
      return notifyError('Valor inválido! Tente alterar o valor.');
    setSavingSetpointChanges(true);
    putSetPointsValue({
      thingId,
      thingName: thing.thing,
      value: Number(valueToChangeSetpoint.value),
      setpointId: valueToChangeSetpoint.setpoint.id,
      setpointName: valueToChangeSetpoint.setpoint.variable,
    })
      .then(() => {
        notifySuccess('Setpoint alterado!');
      })
      .catch(() => {
        notifyError('Ocorreu um erro ao tentar alterar o setpoints!');
      })
      .finally(() => {
        setValueToChangeSetpoint(false);
        setSavingSetpointChanges(false);
      });
  }, [valueToChangeSetpoint, thingId, thing.thing]);

  // Setpoints visiveis (papel permite + habilitado) — viram celulas no MESMO
  // grid dos comandos. hasControls decide entre o grid e o estado vazio.
  const visibleSetpoints = setPointsWidthEdition.filter(
    ({ setpoint }) => USER_ALLOWED_TO_CHANGE_SETPOINTS && setpoint.enabled,
  );
  const hasEnabledCommands = commandsList.some((c) => c.enabled);
  const hasControls = hasEnabledCommands || visibleSetpoints.length > 0;

  const renderModal = () => (
    <ModalContainer>
      <CloseButton type="button" onClick={() => setHistoryOpen(false)} />
      <Column>
        {/* "Despachado" = comando enviado ao broker/shadow com sucesso. NAO
            afirma execucao no equipamento (so se confirma de forma assincrona
            pela conexao do device). Antes dizia "Executado", o que era
            incorreto — o backend lia o reported ~100ms apos enviar. */}
        <Line>
          <Dot red after=" - Não despachado">
            Não despachado
          </Dot>
          <Dot green after=" - Despachado">
            Despachado
          </Dot>
        </Line>

        {commandHistoric.length < 1 ? (
            <label>Não há histórico</label>
        ) : ( 
          <ContainerElements>           
            <HistoricTimelineWrapper>
              {commandHistoric.map(
                ({ timestamp, userName, variable, hasCommandExecuted }, idx) => (
                  <HistoricTimelineCell
                    key={`${timestamp}-${variable?.id ?? idx}`}
                    executed={hasCommandExecuted}
                  >
                    <HistoricCellColumn executed={hasCommandExecuted}>
                      <h3>{newFormatDate(new Date(timestamp))}</h3>
                      <label>Comando: {variable.name}</label>
                      <span>Usuário: {userName}</span>
                      <label>
                        {hasCommandExecuted ? 'Despachado' : 'Não despachado'}
                      </label>
                    </HistoricCellColumn>
                  </HistoricTimelineCell>
                )
              )}
            </HistoricTimelineWrapper>
            {!!Math.ceil(amount / itemsPerPage) && (
              <ReactPaginate
                pageCount={Math.ceil( amount / itemsPerPage)}
                pageRangeDisplayed={0}
                onPageChange={handlePageClick}
                initialPage={0}
                previousLabel="Anterior"
                nextLabel="Próximo"
                breakLabel="..."
                containerClassName="container"
                forcePage={currentPage}
                pageClassName={title === 'light' ? 'page' : 'page-darkmode'}
              pageLinkClassName={
                title === 'light' ? 'pageNumber' : 'pageNumber-darkmode'
              }
              activeClassName={
                title === 'light' ? 'currentPage' : 'currentPage-darkmode'
              }
              previousClassName={
                title === 'light' ? 'labelClass' : 'labelClass-darkmode'
              }
              nextClassName={
                title === 'light' ? 'labelClass' : 'labelClass-darkmode'
              }
              breakClassName={title === 'light' ? 'break' : 'break-darkmode'}
              />
            )} 
          </ContainerElements>
        )}
      </Column>
    </ModalContainer>
  );

  return (
    <Container>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Modal
            style={getModalStyles(colors)}
            isOpen={historyOpen}
            onRequestClose={() => setHistoryOpen(false)}
          >
            {renderModal()}
          </Modal>
          <Caption>Comandos</Caption>
          {pendingCommand && (
            <PendingCommandBanner>
              <span className="ico" aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </span>
              <span className="txt">
                Comando pendente no dispositivo
                {pendingCommand.command ? ` (${pendingCommand.command})` : ''}
                {pendingCommand.since
                  ? ` desde ${newFormatDate(new Date(pendingCommand.since))}`
                  : ''}
                {' '}— aguardando confirmação do equipamento (verifique a
                conexão).
              </span>
            </PendingCommandBanner>
          )}
          {valueToChangeSetpoint && (
            <ChangeSetPointsValueConfirmation
              loading={savingSetpointChanges}
              onCancel={() => {
                setValueToChangeSetpoint(false);
              }}
              onConfirm={handleChangeSetpointValue}
            />
          )}
          {confirmCommand && (
            <ChangeSetPointsValueConfirmation
              loading={sendingCommandId != null}
              title={`Enviar comando "${confirmCommand.name}" para ${
                thing.thing || 'a máquina'
              }?`}
              confirmLabel="Enviar comando"
              loadingLabel="Enviando…"
              ariaLabel="Confirmar envio de comando"
              onCancel={() => setConfirmCommand(null)}
              onConfirm={() =>
                handleCommand(confirmCommand.id, confirmCommand.value)
              }
            />
          )}
          {hasControls ? (
            <CommandsWrapper>
              {/* Comandos habilitados (botoes). Desabilitados ficam ocultos. */}
              {commandsList.map((command) => {
                if (!command.enabled) return null;
                const sending = sendingCommandId === command.id;
                const clickable = !locked && sendingCommandId == null;
                return (
                  <Command key={command.id} enabled $clickable={clickable}>
                    <button
                      type="button"
                      onClick={
                        clickable
                          ? () =>
                              // Dupla confirmacao: comando atua em maquina
                              // FISICA — abre modal antes de despachar.
                              setConfirmCommand({
                                id: command.id,
                                value: command.value,
                                name: command.name,
                              })
                          : undefined
                      }
                      disabled={!clickable}
                    >
                      {sending ? 'Enviando…' : command.name}
                    </button>
                  </Command>
                );
              })}

              {/* Setpoints (inputs) — cada um ocupa um slot no MESMO grid. */}
              {visibleSetpoints.map(({ value, setpoint, ...rest }) => (
                <SetPointItem key={setpoint.id || setpoint.label} visible>
                  <span>{setpoint.label}</span>
                  <SetPointInputContainer>
                    <input
                      type="number"
                      defaultValue={value}
                      onBlur={(event) => {
                        setValueToChangeSetpoint({
                          ...rest,
                          setpoint,
                          value: event.target.value,
                        });
                      }}
                    />
                    {setpoint.unit}
                  </SetPointInputContainer>
                </SetPointItem>
              ))}
            </CommandsWrapper>
          ) : (
            <Content>
              <Label>Não há comandos habilitados para esta Thing</Label>
            </Content>
          )}
        </>
      )}
    </Container>
  );
};

export default Commands;
